import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Folder, Plus, Search, LayoutGrid, List, Smartphone, 
  Trash2, Edit2, ChevronRight, X, Image as ImageIcon, 
  Check, RefreshCcw, GripVertical, Eye, EyeOff, Layers, ExternalLink
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchCategories, buildCategoryTree, createCategory, updateCategory, deleteCategory } from '../../lib/categories';
import { getFileUrl } from '../../lib/pb';

export default function AdminCategories() {
  const [kind, setKind] = useState('catalogue');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [search, setSearch] = useState('');
  const [filterRootId, setFilterRootId] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [draggingId, setDraggingId] = useState(null);
  const [draggingParent, setDraggingParent] = useState(null);

  async function load() {
    setLoading(true); setError('');
    try {
      const list = await fetchCategories(kind);
      setItems(list);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [kind]);

  const rootCategories = useMemo(() => items.filter(c => !c.parent), [items]);

  const filteredItems = useMemo(() => {
    return items.filter(c => {
      if (search) {
        const s = search.toLowerCase();
        if (!c.name?.toLowerCase().includes(s) && !c.slug?.toLowerCase().includes(s)) return false;
      }
      if (filterRootId && c.id !== filterRootId && c.parent !== filterRootId) return false;
      return true;
    });
  }, [items, search, filterRootId]);

  const tree = buildCategoryTree(filteredItems);

  // --- DRAG & DROP LOGIC ---
  const handleDragStart = (node) => { setDraggingId(node.id); setDraggingParent(node.parent || 'root'); };
  const handleDragOver = (targetNode, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetNode.id) return;
    const sourceParent = draggingParent;
    const targetParent = targetNode.parent || 'root';
    if (sourceParent !== targetParent) return;

    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(c => c.id === draggingId);
      const toIdx = next.findIndex(c => c.id === targetNode.id);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const handleDrop = async (node, e) => {
    e.preventDefault();
    const parentKey = node.parent || 'root';
    const siblings = items.filter(c => (c.parent || 'root') === parentKey);
    const updates = siblings.map((c, i) => updateCategory(c.id, { order: (i+1)*10 }));
    try { await Promise.all(updates); load(); } catch(e) {}
    setDraggingId(null); setDraggingParent(null);
  };

  const openCreate = () => { setMode('create'); setEditing({ name: '', slug: '', parent: '', order: 0, kind }); };
  const openEdit = (node) => { setMode('edit'); setEditing(node); };

  async function handleTogglePublished(node) {
    try { await updateCategory(node.id, { published: !node.published }); load(); } catch(e) {}
  }

  async function handleDeleteConfirm(node) {
    if (!window.confirm(`Supprimer "${node.name}" ?`)) return;
    try { await deleteCategory(node.id); load(); } catch(e) {}
  }

  return (
    <div className="categories-page">
      {/* HEADER SECTION */}
      <header className="cat-header">
        <div className="header-text">
          <h1>Catalogue & Services</h1>
          <p className="muted">Organisez vos prestations et produits dans l'arborescence AEF.</p>
        </div>
        <button className="btn-add-main" onClick={openCreate}>
          <Plus size={20} />
          <span>Créer une Catégorie</span>
        </button>
      </header>

      {/* FILTERS BAR */}
      <div className="cat-filters">
        <div className="kind-switch">
          <button className={kind === 'catalogue' ? 'active' : ''} onClick={() => setKind('catalogue')}>Catalogue</button>
          <button className={kind === 'realisations' ? 'active' : ''} onClick={() => setKind('realisations')}>Réalisations</button>
        </div>

        <div className="search-box">
          <Search size={18} />
          <input placeholder="Rechercher une catégorie..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="view-switcher">
           <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
           <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
        </div>
      </div>

      {error && <div className="error-pill">{error}</div>}

      {/* MAIN CONTENT */}
      <main className="cat-main">
        {loading ? (
          <div className="loading-state">Synchronisation du catalogue...</div>
        ) : (
          <div className={`cat-content-view ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="cat-grid">
                {filteredItems.map(c => (
                  <div key={c.id} className="cat-card-premium" onClick={() => openEdit(c)}>
                    <div className="cat-card-img">
                      {c.image ? <img src={getFileUrl(c, c.image)} alt={c.name} /> : <div className="no-img"><ImageIcon size={24}/></div>}
                      <div className={`cat-card-badge ${c.published ? 'ok' : 'none'}`}>
                        {c.published ? <Eye size={12}/> : <EyeOff size={12}/>}
                      </div>
                    </div>
                    <div className="cat-card-info">
                       <h3>{c.name}</h3>
                       <p className="slug">{c.slug}</p>
                       <div className="cat-card-footer">
                          <span className="count-tag"><Layers size={10}/> {c.parent ? 'Sous-catégorie' : 'Parent'}</span>
                          <ChevronRight size={16} className="arrow" />
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cat-list-tree">
                {tree.map(node => (
                  <CategoryRow 
                    key={node.id} 
                    node={node} 
                    level={0} 
                    onEdit={openEdit} 
                    onToggle={handleTogglePublished}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            )}
            {items.length === 0 && <div className="empty-state">Aucune catégorie trouvée.</div>}
          </div>
        )}
      </main>

      {editing && (
        <CategoryModal 
          mode={mode} 
          item={editing} 
          kind={kind}
          onClose={() => setEditing(null)} 
          onSaved={() => { setEditing(null); load(); }} 
        />
      )}

      <style>{`
        .categories-page { display: flex; flex-direction: column; gap: 20px; animation: fadeIn 0.4s ease; }
        
        .cat-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .cat-header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #0f172a; }
        
        .btn-add-main { background: var(--admin-primary); border: none; font-weight: 800; border-radius: 16px; padding: 12px 20px; display: flex; align-items: center; gap: 10px; cursor: pointer; box-shadow: 0 10px 15px -3px rgba(234, 179, 8, 0.2); transition: all 0.2s; }
        .btn-add-main:hover { transform: translateY(-2px); }

        .cat-filters { display: flex; gap: 15px; align-items: center; background: #fff; padding: 10px 15px; border-radius: 20px; border: 1px solid var(--admin-border); flex-wrap: wrap; }
        
        .kind-switch { background: #f1f5f9; padding: 4px; border-radius: 12px; display: flex; gap: 4px; }
        .kind-switch button { border: none; background: none; padding: 8px 15px; border-radius: 9px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; transition: all 0.2s; }
        .kind-switch button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }

        .search-box { flex: 1; display: flex; align-items: center; gap: 10px; padding: 0 10px; color: #94a3b8; }
        .search-box input { border: none; outline: none; width: 100%; font-size: 14px; font-weight: 600; color: #0f172a; background: transparent; }

        .view-switcher { display: flex; background: #f1f5f9; padding: 4px; border-radius: 12px; gap: 4px; }
        .view-switcher button { width: 36px; height: 36px; border-radius: 9px; border: none; background: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .view-switcher button.active { background: #fff; color: #0f172a; }

        /* GRID VIEW */
        .cat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
        .cat-card-premium { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; overflow: hidden; cursor: pointer; transition: all 0.3s; position: relative; }
        .cat-card-premium:hover { transform: translateY(-5px); box-shadow: 0 20px 30px -10px rgba(0,0,0,0.05); border-color: var(--admin-primary); }
        
        .cat-card-img { height: 160px; background: #f8fafc; position: relative; overflow: hidden; }
        .cat-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .no-img { height: 100%; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        
        .cat-card-badge { position: absolute; top: 12px; right: 12px; width: 28px; height: 28px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
        .cat-card-badge.ok { color: #10b981; }
        .cat-card-badge.none { color: #94a3b8; }

        .cat-card-info { padding: 20px; }
        .cat-card-info h3 { margin: 0 0 4px 0; font-size: 18px; font-weight: 800; color: #0f172a; }
        .cat-card-info .slug { font-size: 12px; font-weight: 700; color: #94a3b8; margin: 0 0 15px 0; }
        
        .cat-card-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px; }
        .count-tag { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #64748b; background: #f1f5f9; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; }
        .arrow { color: #f1f5f9; transition: transform 0.2s; }
        .cat-card-premium:hover .arrow { color: var(--admin-primary); transform: translateX(3px); }

        /* LIST VIEW */
        .cat-list-tree { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; padding: 15px; }

        @media (max-width: 900px) {
          .cat-header { flex-direction: column; align-items: flex-start; gap: 15px; }
          .cat-header h1 { font-size: 24px; }
          .cat-filters { gap: 10px; }
          .kind-switch { width: 100%; }
          .kind-switch button { flex: 1; }
          .cat-grid { grid-template-columns: 1fr; }
        }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

function CategoryRow({ node, level, onEdit, onToggle, onDragStart, onDragOver, onDrop }) {
  const children = node.children || [];
  return (
    <div className="cat-row-container">
      <div 
        className="cat-row-item" 
        style={{ paddingLeft: level * 28 }}
        draggable
        onDragStart={() => onDragStart(node)}
        onDragOver={e => onDragOver(node, e)}
        onDrop={e => onDrop(node, e)}
      >
        <div className="drag-handle"><GripVertical size={18}/></div>
        <div className="cat-row-img">
           {node.image ? <img src={getFileUrl(node, node.image)} alt={node.name} /> : <ImageIcon size={14}/>}
        </div>
        <div className="cat-row-main" onClick={() => onEdit(node)}>
           <span className="name">{node.name}</span>
           <span className="slug">{node.slug}</span>
        </div>
        <div className="cat-row-acts">
           <button className={`btn-status ${node.published ? 'ok' : ''}`} onClick={() => onToggle(node)}>
             {node.published ? <Eye size={16}/> : <EyeOff size={16}/>}
           </button>
           <button className="btn-edit" onClick={() => onEdit(node)}><ChevronRight size={18}/></button>
        </div>
      </div>
      {children.length > 0 && (
        <div className="cat-children">
          {children.map(child => (
            <CategoryRow key={child.id} node={child} level={level + 1} onEdit={onEdit} onToggle={onToggle} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} />
          ))}
        </div>
      )}
      <style>{`
        .cat-row-item { display: flex; align-items: center; gap: 15px; padding: 12px 15px; border-bottom: 1px solid #f8fafc; transition: all 0.2s; cursor: pointer; }
        .cat-row-item:hover { background: #f8fafc; }
        
        .drag-handle { color: #cbd5e1; cursor: grab; }
        .cat-row-img { width: 44px; height: 32px; background: #f1f5f9; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; color: #94a3b8; }
        .cat-row-img img { width: 100%; height: 100%; object-fit: cover; }
        
        .cat-row-main { flex: 1; display: flex; flex-direction: column; }
        .cat-row-main .name { font-weight: 800; font-size: 14px; color: #0f172a; }
        .cat-row-main .slug { font-size: 11px; font-weight: 700; color: #94a3b8; }
        
        .cat-row-acts { display: flex; gap: 8px; }
        .btn-status { border: none; background: #f1f5f9; color: #94a3b8; width: 34px; height: 34px; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .btn-status.ok { background: #ecfdf5; color: #10b981; }
        .btn-edit { border: none; background: none; color: #cbd5e1; cursor: pointer; }
      `}</style>
    </div>
  );
}

function CategoryModal({ mode, item, kind, onClose, onSaved }) {
    const [tab, setTab] = useState('infos');
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState(item.name || '');
    const [slug, setSlug] = useState(item.slug || '');
    const [published, setPublished] = useState(item.published ?? true);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(item.image ? getFileUrl(item, item.image) : '');
    const fileInputRef = useRef(null);

    const slugify = (v) => v.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true);
        const fd = new FormData();
        fd.append('name', name); fd.append('slug', slug); fd.append('published', published); fd.append('kind', kind);
        if (imageFile) fd.append('image', imageFile);
        try {
            if (item.id) await updateCategory(item.id, fd);
            else await createCategory(fd);
            onSaved();
        } catch(e) { alert(e.message); } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay-s" onClick={e => e.target.className === 'modal-overlay-s' && onClose()}>
            <div className="modal-content-s">
                <div className="modal-s-top">
                    <div className="modal-s-tabs">
                        <button className={tab === 'infos' ? 'active' : ''} onClick={() => setTab('infos')}>Détails</button>
                        <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Image</button>
                    </div>
                    <button className="s-close" onClick={onClose}><X size={20}/></button>
                </div>
                
                <form className="modal-s-body" onSubmit={handleSave}>
                    {tab === 'infos' ? (
                        <div className="s-form">
                            <div className="s-field">
                                <label>Nom de la catégorie</label>
                                <input value={name} onChange={e => { setName(e.target.value); if(!item.slug) setSlug(slugify(e.target.value)); }} required />
                            </div>
                            <div className="s-field">
                                <label>Slug (URL)</label>
                                <input value={slug} onChange={e => setSlug(e.target.value)} required />
                            </div>
                            <div className="s-toggle">
                                <label>Visible sur le site</label>
                                <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                            </div>
                        </div>
                    ) : (
                        <div className="s-media-upload">
                             <div className="upload-zone" onClick={() => fileInputRef.current.click()}>
                                {imagePreview ? <img src={imagePreview} alt="Preview" /> : <div className="upload-placeholder"><ImageIcon size={40}/><p>Cliquer pour ajouter une image</p></div>}
                                <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={e => { const f = e.target.files[0]; if(f){setImageFile(f); setImagePreview(URL.createObjectURL(f));}}} />
                             </div>
                        </div>
                    )}
                    
                    <div className="modal-s-footer">
                        {item.id && <button type="button" className="btn-del-team" onClick={async () => { if(window.confirm('Supprimer ?')){ await deleteCategory(item.id); onSaved(); }}}><Trash2 size={16}/></button>}
                        <div className="spacer"></div>
                        <button type="submit" className="btn-submit-team" disabled={saving}>{saving ? '...' : 'Valider'}</button>
                    </div>
                </form>
            </div>
            <style>{`
                .modal-overlay-s { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(8px); z-index: 20000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-content-s { background: #fff; width: 100%; max-width: 480px; border-radius: 32px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .modal-s-top { padding: 20px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; }
                .modal-s-tabs { display: flex; gap: 5px; background: #f1f5f9; padding: 4px; border-radius: 12px; }
                .modal-s-tabs button { border: none; background: none; padding: 8px 15px; border-radius: 9px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; }
                .modal-s-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }

                .modal-s-body { padding: 20px; }
                .s-form { display: grid; gap: 15px; }
                .s-field { display: grid; gap: 6px; }
                .s-field label { font-size: 12px; font-weight: 800; color: #94a3b8; text-transform: uppercase; }
                .s-field input { background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px; border-radius: 14px; font-weight: 600; outline: none; }
                
                .s-toggle { display: flex; justify-content: space-between; align-items: center; background: #f8fafc; padding: 12px; border-radius: 14px; }
                .s-toggle label { font-size: 14px; font-weight: 800; }
                
                .s-media-upload { height: 260px; }
                .upload-zone { width: 100%; height: 100%; border: 2px dashed #e2e8f0; border-radius: 20px; overflow: hidden; cursor: pointer; display: flex; align-items: center; justify-content: center; }
                .upload-zone img { width: 100%; height: 100%; object-fit: cover; }
                .upload-placeholder { text-align: center; color: #94a3b8; }
                .upload-placeholder p { font-size: 13px; font-weight: 700; margin-top: 10px; }

                .modal-s-footer { display: flex; align-items: center; gap: 15px; margin-top: 20px; border-top: 1px solid #f1f5f9; padding-top: 20px; }
                .btn-del-team { width: 44px; height: 44px; border-radius: 12px; background: #fff1f2; color: #f43f5e; border: none; cursor: pointer; }
                .btn-submit-team { flex: 1; height: 48px; background: #0f172a; color: #fff; border: none; border-radius: 14px; font-weight: 800; cursor: pointer; }
                .spacer { flex: 1; }
            `}</style>
        </div>
    );
}
